import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import History from '@mui/icons-material/History';
import moment from 'moment-timezone';
import { Modal, ModalBody, ModalHeader } from '@/ui';
import { useZuulAuditEvents } from './queries';
import { AuditDisplaySettings } from './types';
import { ViewModeButtons, AuditEventRow } from './components';
import { computeEventComparisons, DEFAULT_COMPARISON_BLACKLIST } from './utils';
import ZuulLoader from '../ZuulLoader/ZuulLoader';

const MIN_AUDIT_DATE = '2026-03-03';

const COLUMNS = ['Timestamp', 'User', 'Method', 'URL', 'Status', 'Changes'];

interface AuditModalButtonProps {
  route?: string;
  activeUrlFilters?: string[];
  blacklist?: string[];
}

interface AuditModalProps {
  route: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  /** Optional array of field paths to exclude from change comparison (e.g., ["offerQuestion.offer"]) */
  blacklist?: string[];
  activeUrlFilters?: string[];
}

const AuditModalButton: React.FC<AuditModalButtonProps> = ({
  route = window.location.pathname,
  blacklist,
  activeUrlFilters = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const label = `View audit events for ${route}`;

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            setIsOpen(true);
          }}
          aria-label={label}
        >
          <History />
        </IconButton>
      </Tooltip>
      {isOpen && (
        <AuditModal
          route={route}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          blacklist={blacklist}
          activeUrlFilters={activeUrlFilters}
        />
      )}
    </>
  );
};

const AuditModal: React.FC<AuditModalProps> = ({
  route,
  isOpen,
  setIsOpen,
  blacklist = DEFAULT_COMPARISON_BLACKLIST,
  activeUrlFilters,
}) => {
  const initialDate = moment(new Date()).subtract(15, 'days').format('YYYY-MM-DD');
  const clampedInitialDate = initialDate < MIN_AUDIT_DATE ? MIN_AUDIT_DATE : initialDate;

  const [startDate, setStartDate] = useState<string>(clampedInitialDate);
  const [dateInputValue, setDateInputValue] = useState<string>(clampedInitialDate);
  const [displaySettings, setDisplaySettings] = useState<AuditDisplaySettings>({
    viewMode: 'naturalLanguage',
    collapseAllChanges: false,
  });

  const { data: allEvents, isLoading } = useZuulAuditEvents(
    route,
    moment(startDate).tz('America/New_York').format('YYYY-MM-DDTHH:mm:ss')
  );

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDateInputValue(value);
    // Only commit to startDate (and trigger API) if fully valid
    if (value && moment(value, 'YYYY-MM-DD', true).isValid()) {
      setStartDate(value);
    }
  };

  const handleDateBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && moment(value, 'YYYY-MM-DD', true).isValid() && value < MIN_AUDIT_DATE) {
      alert('Event data is only available from March 3, 2026 onward. Your date has been adjusted automatically.');
      setStartDate(MIN_AUDIT_DATE);
      setDateInputValue(MIN_AUDIT_DATE);
    }
  };

  // Compute event comparisons
  const comparisons = useMemo(() => {
    if (!allEvents) return [];
    return computeEventComparisons(allEvents, blacklist, activeUrlFilters);
  }, [allEvents, blacklist, activeUrlFilters]);

  const hasEvents = Boolean(allEvents && allEvents.length > 0);

  return (
    <Modal isOpen={isOpen} toggle={() => setIsOpen(false)} size='xl'>
      <ModalHeader toggle={() => setIsOpen(false)}>Audit Events for {route}</ModalHeader>
      <ModalBody>
        <Stack
          direction='row'
          spacing={2}
          useFlexGap
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
            pb: 2,
            mb: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <TextField
            type='date'
            size='small'
            label='Since'
            value={dateInputValue}
            onChange={handleDateChange}
            onBlur={handleDateBlur}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: MIN_AUDIT_DATE } }}
            sx={{ minWidth: '12em' }}
          />
          {hasEvents && <ViewModeButtons displaySettings={displaySettings} setDisplaySettings={setDisplaySettings} />}
        </Stack>

        <Box
          sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {hasEvents ? (
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((column) => (
                    <TableCell key={column} sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisons.map((comparison, index) => (
                  <AuditEventRow
                    key={`${comparison.currentEvent.timestamp}-${index}`}
                    comparison={comparison}
                    displaySettings={displaySettings}
                    isRootEvent={index === comparisons.length - 1}
                  />
                ))}
              </TableBody>
            </Table>
          ) : isLoading ? (
            <ZuulLoader text={'Loading Events...'} />
          ) : (
            <Typography sx={{ p: 2, color: 'text.secondary' }}>No events found for the selected date.</Typography>
          )}
        </Box>
      </ModalBody>
    </Modal>
  );
};

export default AuditModalButton;
